import React, { useEffect, useRef, useState } from 'react';
import { LinkIcon } from '@heroicons/react/24/outline';

import { useI18n } from '../i18n/I18nProvider';
import {
  createReorderChunks,
  flattenReorderChunks,
  joinReorderChunks,
  moveReorderChunk,
  splitReorderChunk,
} from '../lib/reorderChunks';
import { playSoundEffect } from '../lib/sfx';

const showLabel = (value) => (
  typeof value === 'string' ? value.replace(/_/g, ' ') : value
);

const detachGlobalDragListeners = (listeners) => {
  if (!listeners || typeof window === 'undefined') return;
  window.removeEventListener('pointermove', listeners.move);
  window.removeEventListener('pointerup', listeners.up);
  window.removeEventListener('pointercancel', listeners.cancel);
};

const ReorderPuzzle = ({
  scrambled,
  onSubmit,
  onProgressChange,
  disabled = false,
  captureMode = false,
  renderCaptureToken,
}) => {
  const { t } = useI18n();
  const [chunks, setChunks] = useState(() => createReorderChunks(scrambled));
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const dragListenersRef = useRef(null);

  useEffect(() => {
    detachGlobalDragListeners(dragListenersRef.current);
    dragListenersRef.current = null;
    setChunks(createReorderChunks(scrambled));
    setDrag(null);
    dragRef.current = null;
  }, [scrambled]);

  useEffect(() => () => {
    detachGlobalDragListeners(dragListenersRef.current);
  }, []);

  useEffect(() => {
    if (typeof onProgressChange !== 'function') return;
    onProgressChange({
      answer: flattenReorderChunks(chunks.answer).join(' '),
      unusedTokens: flattenReorderChunks(chunks.bank).map(showLabel),
    });
  }, [chunks, onProgressChange]);

  const chunkLabel = (chunk) => (
    chunk.tokens.map((token) => showLabel(token.word)).join(' ')
  );

  const clearDrag = () => {
    detachGlobalDragListeners(dragListenersRef.current);
    dragListenersRef.current = null;
    dragRef.current = null;
    setDrag(null);
  };

  const finishDrag = (event) => {
    const current = dragRef.current;
    if (!current) return;
    if (event.pointerId !== undefined && event.pointerId !== current.pointerId) return;

    if (current.active && current.targetZone) {
      setChunks((previous) => moveReorderChunk(
        previous,
        current.sourceZone,
        current.sourceIndex,
        current.targetZone,
        current.targetIndex,
      ));
      playSoundEffect(
        current.targetZone === 'bank' ? 'dropBack' : 'pick',
        { volume: 0.64, throttleMs: 40, throttleKey: 'wp-scramble-drop' },
      );
    }

    try {
      current.captureElement?.releasePointerCapture?.(current.pointerId);
    } catch (_) {
      // Pointer capture may already be released after a browser gesture.
    }
    clearDrag();
  };

  const cancelDrag = () => {
    clearDrag();
  };

  const updateDragTarget = (event) => {
    const current = dragRef.current;
    if (!current) return;
    if (event.pointerId !== undefined && event.pointerId !== current.pointerId) return;

    const distance = Math.hypot(
      event.clientX - current.startX,
      event.clientY - current.startY,
    );
    const active = current.active || distance >= 6;
    let targetZone = current.targetZone;
    let targetIndex = current.targetIndex;

    if (active) {
      event.preventDefault?.();
      const element = document.elementFromPoint(event.clientX, event.clientY);
      const explicitSlot = element?.closest?.('[data-reorder-drop-slot]');
      const targetChunk = element?.closest?.('[data-reorder-chunk]');
      const targetZoneElement = element?.closest?.('[data-reorder-zone]');

      if (explicitSlot) {
        targetZone = explicitSlot.dataset.reorderDropZone;
        targetIndex = Number(explicitSlot.dataset.reorderDropIndex);
      } else if (targetChunk) {
        const bounds = targetChunk.getBoundingClientRect();
        targetZone = targetChunk.dataset.reorderChunkZone;
        targetIndex = Number(targetChunk.dataset.reorderChunkIndex)
          + (event.clientX >= bounds.left + bounds.width / 2 ? 1 : 0);
      } else if (targetZoneElement) {
        targetZone = targetZoneElement.dataset.reorderZone;
        targetIndex = chunks[targetZone]?.length ?? 0;
      } else {
        targetZone = null;
        targetIndex = null;
      }
    }

    const next = {
      ...current,
      active,
      x: event.clientX,
      y: event.clientY,
      targetZone,
      targetIndex,
    };
    dragRef.current = next;
    setDrag(next);
  };

  const startDrag = (event, sourceZone, sourceIndex, chunk) => {
    if (disabled || captureMode || event.button > 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const next = {
      sourceZone,
      sourceIndex,
      chunk,
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      captureElement: event.currentTarget,
      startX: event.clientX,
      startY: event.clientY,
      x: event.clientX,
      y: event.clientY,
      active: false,
      targetZone: null,
      targetIndex: null,
    };
    dragRef.current = next;
    setDrag(next);

    const listeners = {
      move: (moveEvent) => updateDragTarget(moveEvent),
      up: (upEvent) => finishDrag(upEvent),
      cancel: (cancelEvent) => {
        if (cancelEvent.pointerId === event.pointerId) cancelDrag();
      },
    };
    dragListenersRef.current = listeners;
    window.addEventListener('pointermove', listeners.move, { passive: false });
    window.addEventListener('pointerup', listeners.up);
    window.addEventListener('pointercancel', listeners.cancel);
  };

  const handleJoin = (zone, leftIndex) => {
    if (disabled || captureMode || dragRef.current?.active) return;
    setChunks((previous) => joinReorderChunks(previous, zone, leftIndex));
    playSoundEffect(
      'pick',
      { volume: 0.58, throttleMs: 70, throttleKey: 'wp-scramble-join' },
    );
  };

  const handleSplit = (zone, chunkIndex, tokenBoundary) => {
    if (disabled || captureMode || dragRef.current?.active) return;
    setChunks((previous) => (
      splitReorderChunk(previous, zone, chunkIndex, tokenBoundary)
    ));
    playSoundEffect(
      'dropBack',
      { volume: 0.55, throttleMs: 70, throttleKey: 'wp-scramble-split' },
    );
  };

  const moveByKeyboard = (event, sourceZone, sourceIndex) => {
    if (disabled || captureMode) return;
    if (event.key === 'Escape') {
      cancelDrag();
      return;
    }
    if (['Enter', ' '].includes(event.key)) {
      event.preventDefault();
      const targetZone = sourceZone === 'bank' ? 'answer' : 'bank';
      setChunks((previous) => moveReorderChunk(
        previous,
        sourceZone,
        sourceIndex,
        targetZone,
        previous[targetZone].length,
      ));
      return;
    }
    if (event.key === 'ArrowLeft' && sourceIndex > 0) {
      event.preventDefault();
      setChunks((previous) => moveReorderChunk(
        previous,
        sourceZone,
        sourceIndex,
        sourceZone,
        sourceIndex - 1,
      ));
    }
    if (event.key === 'ArrowRight' && sourceIndex < chunks[sourceZone].length - 1) {
      event.preventDefault();
      setChunks((previous) => moveReorderChunk(
        previous,
        sourceZone,
        sourceIndex,
        sourceZone,
        sourceIndex + 2,
      ));
    }
  };

  const renderChunk = (chunk, zone, chunkIndex, isGhost = false) => {
    const isDraggingThis = drag?.active
      && drag.sourceZone === zone
      && drag.sourceIndex === chunkIndex;
    return (
      <div
        key={chunk.id}
        role={isGhost ? undefined : 'group'}
        tabIndex={isGhost || captureMode ? -1 : 0}
        draggable={false}
        data-i18n-skip
        data-reorder-chunk={isGhost ? undefined : ''}
        data-reorder-chunk-zone={isGhost ? undefined : zone}
        data-reorder-chunk-index={isGhost ? undefined : chunkIndex}
        data-testid={isGhost ? undefined : `reorder-${zone}-chunk-${chunkIndex}`}
        onPointerDown={
          isGhost
            ? undefined
            : (event) => startDrag(event, zone, chunkIndex, chunk)
        }
        onKeyDown={
          isGhost
            ? undefined
            : (event) => moveByKeyboard(event, zone, chunkIndex)
        }
        aria-label={
          isGhost ? undefined : t('Drag {chunk}', { chunk: chunkLabel(chunk) })
        }
        className={`inline-flex min-h-11 max-w-full select-none flex-wrap items-stretch overflow-hidden rounded-md border-2 bg-white shadow-sm transition-[box-shadow,opacity,transform] ${
          zone === 'answer' ? 'border-slate-300' : 'border-blue-400'
        } ${
          isGhost
            ? 'pointer-events-none scale-[1.03] border-blue-500 shadow-xl'
            : 'cursor-grab hover:-translate-y-0.5 hover:shadow-md active:cursor-grabbing'
        } ${isDraggingThis ? 'opacity-20' : ''} ${
          disabled ? 'cursor-not-allowed opacity-60' : ''
        }`}
        style={{ touchAction: captureMode ? 'auto' : 'none' }}
      >
        {chunk.tokens.map((token, tokenIndex) => (
          <React.Fragment key={token.id}>
            {tokenIndex > 0 && (
              <button
                type="button"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={() => handleSplit(zone, chunkIndex, tokenIndex)}
                disabled={disabled || captureMode}
                aria-pressed="true"
                className="inline-flex w-7 shrink-0 items-center justify-center border-x border-blue-300 bg-blue-50 text-blue-700 transition-colors hover:bg-amber-50 hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed"
                title={t('Disconnect these words')}
                aria-label={t('Disconnect {left} and {right}', {
                  left: showLabel(chunk.tokens[tokenIndex - 1].word),
                  right: showLabel(token.word),
                })}
              >
                <LinkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
            <span className="flex min-h-10 items-center px-3 py-2 text-lg font-medium text-gray-900">
              {captureMode && renderCaptureToken
                ? renderCaptureToken(token.word, token.id)
                : showLabel(token.word)}
            </span>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderEdgeDropSlot = (zone, index) => {
    const active = drag?.active
      && drag.targetZone === zone
      && drag.targetIndex === index;
    return (
      <div
        key={`${zone}-edge-${index}`}
        data-reorder-drop-slot
        data-reorder-drop-zone={zone}
        data-reorder-drop-index={index}
        aria-label={t('Drop at position {position}', { position: index + 1 })}
        className="flex min-h-11 w-3 shrink-0 items-stretch justify-center"
      >
        <span
          className={`w-1 rounded-full transition-colors ${
            active ? 'bg-blue-500' : 'bg-transparent'
          }`}
        />
      </div>
    );
  };

  const renderBoundary = (zone, leftIndex) => {
    const rightIndex = leftIndex + 1;
    const active = drag?.active
      && drag.targetZone === zone
      && drag.targetIndex === rightIndex;
    const left = chunks[zone][leftIndex];
    const right = chunks[zone][rightIndex];
    if (captureMode) {
      return <div className="w-2 shrink-0" aria-hidden="true" />;
    }
    return (
      <div
        data-reorder-drop-slot
        data-reorder-drop-zone={zone}
        data-reorder-drop-index={rightIndex}
        className="flex min-h-11 w-8 shrink-0 items-center justify-center"
      >
        {drag?.active ? (
          <span
            className={`h-10 w-1 rounded-full transition-colors ${
              active ? 'bg-blue-500' : 'bg-slate-200'
            }`}
          />
        ) : (
          <button
            type="button"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => handleJoin(zone, leftIndex)}
            disabled={disabled || captureMode}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-dashed border-slate-300 bg-white text-slate-500 transition-colors hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
            title={t('Join these chunks')}
            aria-label={t('Join {left} and {right}', {
              left: chunkLabel(left),
              right: chunkLabel(right),
            })}
          >
            <LinkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  };

  const renderZone = (zone, emptyText) => (
    <div
      data-reorder-zone={zone}
      data-testid={`reorder-${zone}-zone`}
      className={`flex min-h-[76px] flex-wrap items-center gap-y-3 rounded-md border-2 p-3 transition-colors ${
        zone === 'answer'
          ? 'border-slate-300 bg-slate-50'
          : 'border-blue-100 bg-blue-50/40'
      } ${drag?.active && drag.targetZone === zone ? 'border-blue-400 bg-blue-50' : ''}`}
    >
      {chunks[zone].length === 0 ? (
        <span className="pointer-events-none px-2 text-gray-400">{emptyText}</span>
      ) : (
        <>
          {renderEdgeDropSlot(zone, 0)}
          {chunks[zone].map((chunk, index) => (
            <div
              key={chunk.id}
              className="inline-flex min-w-0 max-w-full items-stretch"
            >
              {index > 0 && renderBoundary(zone, index - 1)}
              {renderChunk(chunk, zone, index)}
            </div>
          ))}
          {renderEdgeDropSlot(zone, chunks[zone].length)}
        </>
      )}
    </div>
  );

  const handleSubmit = () => {
    if (disabled) return;
    playSoundEffect(
      'check',
      { volume: 0.78, throttleMs: 120, throttleKey: 'wp-scramble-check' },
    );
    onSubmit(flattenReorderChunks(chunks.answer).join(' '));
  };

  const ghostTransform = drag?.pointerType === 'touch'
    ? 'translate(-50%, -125%)'
    : 'translate(-50%, -50%)';

  return (
    <div className="relative" data-testid="reorder-puzzle">
      <div className="mb-4">
        {renderZone('answer', t('Build your sentence here...'))}
      </div>
      {renderZone('bank', t('Unused words'))}
      {!captureMode && (
        <button
          type="button"
          onClick={handleSubmit}
          disabled={disabled || chunks.answer.length === 0}
          className="mt-4 w-full rounded-md bg-blue-600 px-4 py-3 text-lg font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {t('Check')}
        </button>
      )}
      {drag?.active && (
        <div
          className="pointer-events-none fixed z-[100] max-w-[85vw]"
          style={{
            left: drag.x,
            top: drag.y,
            transform: ghostTransform,
          }}
        >
          {renderChunk(drag.chunk, drag.sourceZone, drag.sourceIndex, true)}
        </div>
      )}
    </div>
  );
};

export default ReorderPuzzle;
